/**
 * -----------------------------------------------------------------------------
 * useOrbitLayer
 * -----------------------------------------------------------------------------
 *
 * PURPOSE:
 * Render provider satellite orbits using a single GeoJSON source.
 *
 * REPLACES:
 *   - addProviderOrbitsSource
 *   - removeAllOrbitLayers
 *   - per-orbit layer creation
 *   - manual highlight logic
 *
 * FEATURES:
 *   - Single source
 *   - Data-driven highlight
 *   - Click support
 *   - Cursor interaction
 *   - Efficient updates
 *   - No looping cleanup
 *
 * USAGE:
 *
 * useOrbitLayer(mapRef, {
 *   passes: showOrbit?.satellitePasses,
 *   providerName: showOrbit?.providerName,
 *   highlightedEntryTime: highlightOrbit?.entryTime,
 *   onClick: (pass) => dispatch(highlightOrbitpath(pass))
 * });
 *
 * -----------------------------------------------------------------------------
 */

import { useEffect } from "react";
import * as MAP_CONSTANTS from "../../common/constants/MapOptions.js";

const useOrbitLayer = (
  mapRef,
  { passes, providerName, highlightedEntryTime, onClick }
) => {
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (!passes || passes.length === 0) {
      removeOrbit(map);
      return;
    }

    const providerColor =
      MAP_CONSTANTS.PROVIDER_ORBIT_COLORS[providerName?.toLowerCase()] ||
      MAP_CONSTANTS.PROVIDER_ORBIT_COLORS.default;

    const featureCollection = {
      type: "FeatureCollection",
      features: passes.map((pass) => ({
        type: "Feature",
        properties: {
          entryTime: pass.entryTime,
          selected: highlightedEntryTime === pass.entryTime,
          color: providerColor,
        },
        geometry: {
          type: "LineString",
          coordinates: pass.orbitData.map((p) => [p.longitude, p.latitude]),
        },
      })),
    };

    const loadLayer = () => {
      if (!map.getSource("orbit-source")) {
        map.addSource("orbit-source", {
          type: "geojson",
          data: featureCollection,
        });
      } else {
        map.getSource("orbit-source").setData(featureCollection);
      }

      if (!map.getLayer("orbit-line")) {
        map.addLayer({
          id: "orbit-line",
          type: "line",
          source: "orbit-source",
          paint: {
            "line-color": [
              "case",
              ["get", "selected"],
              "#00FF00",
              ["get", "color"],
            ],
            "line-width": ["case", ["get", "selected"], 4, 2],
            "line-dasharray": [4, 2],
          },
        });

        map.on("click", "orbit-line", (e) => {
          const feature = e.features?.[0];
          if (!feature) return;

          const entryTime = feature.properties.entryTime;
          const pass = passes.find((p) => p.entryTime === entryTime);

          if (pass && onClick) onClick(pass);
        });

        map.on("mouseenter", "orbit-line", () => {
          map.getCanvas().style.cursor = "pointer";
        });

        map.on("mouseleave", "orbit-line", () => {
          map.getCanvas().style.cursor = "";
        });
      }
    };

    if (!map.isStyleLoaded()) {
      map.once("styledata", loadLayer);
    } else {
      loadLayer();
    }

    function removeOrbit(map) {
      if (map.getLayer("orbit-line")) {
        map.removeLayer("orbit-line");
      }
      if (map.getSource("orbit-source")) {
        map.removeSource("orbit-source");
      }
    }

    return () => {
      if (!map) return;
      removeOrbit(map);
    };
  }, [passes, highlightedEntryTime, providerName]);
};

export default useOrbitLayer;